import type { StepRenderProps } from '@/components/new-safe/CardStepper/useCardStepper'
import type { LoadSafeFormData } from '@/components/new-safe/load'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '@safe-global/views/components/ui/button'
import { Spinner } from '@safe-global/views/components/ui/spinner'
import { Separator } from '@safe-global/views/components/ui/separator'
import { Tooltip, TooltipTrigger, TooltipContent } from '@safe-global/views/components/ui/tooltip'
import { Typography } from '@safe-global/views/components/ui/typography'
import layoutCss from '@/components/new-safe/create/styles.module.css'
import NameInput from '@/components/common/NameInput'
import InfoIcon from '@safe-global/views/assets/images/notifications/info.svg'
import NetworkSelector from '@/components/common/NetworkSelector'
import { useMnemonicSafeName } from '@/hooks/useMnemonicName'
import { useAddressResolver } from '@/hooks/useAddressResolver'
import AddressInput from '@/components/common/AddressInput'
import { largeFormFieldSurfaceClassName } from '@safe-global/views/components/common/formFieldStyles'
import React, { useState } from 'react'
import { useLazySafesGetSafeV1Query } from '@safe-global/store/gateway/AUTO_GENERATED/safes'
import useChainId from '@/hooks/useChainId'
import { useAppSelector } from '@/store'
import { selectAddedSafes } from '@/store/addedSafesSlice'
import {
  ADDRESS_POISONING_EVENTS,
  DismissAction,
  LOAD_SAFE_EVENTS,
  LookalikeWarningLocation,
  MixpanelEventParams,
  trackEvent,
} from '@/services/analytics'
import { useTrackOnce } from '@/services/analytics/useTrackOnce'
import SimilarityConfirmDialog from '@/components/common/TrustedSafesModal/SimilarityConfirmDialog'
import { useAddressPoisoningCheck } from '@/features/address-poisoning'
import { SimilarSavedAddressNotice } from '@/components/common/SimilarAddressNotice'
import ExternalLink from '@safe-global/views/components/common/ExternalLink'
import { PRIVACY_URL, TERMS_URL } from '@safe-global/utils/config/constants'

enum Field {
  name = 'name',
  address = 'address',
}

type FormData = {
  [Field.name]: string
  [Field.address]: string
}

const networkSelectTriggerClassName = `${largeFormFieldSurfaceClassName} w-full`

const SetAddressStep = ({ data, onSubmit, onBack }: StepRenderProps<LoadSafeFormData>) => {
  const currentChainId = useChainId()
  const addedSafes = useAppSelector((state) => selectAddedSafes(state, currentChainId))
  const [triggerGetSafe] = useLazySafesGetSafeV1Query()
  const [pendingData, setPendingData] = useState<FormData>()
  const formMethods = useForm<FormData>({
    mode: 'all',
    defaultValues: {
      [Field.name]: data.name,
      [Field.address]: data.address,
    },
  })

  const {
    handleSubmit,
    formState: { errors, isValid },
    watch,
    getValues,
  } = formMethods

  const safeAddress = watch(Field.address)
  const randomName = useMnemonicSafeName()
  const { ens, name, resolving } = useAddressResolver(safeAddress)
  const similar = useAddressPoisoningCheck(safeAddress)
  const lookalikeTracking = { [MixpanelEventParams.LOCATION]: LookalikeWarningLocation.ADD_SAFE }
  useTrackOnce(ADDRESS_POISONING_EVENTS.WARNING_SHOWN, lookalikeTracking, !!similar)

  // Address book, ENS, mnemonic
  const fallbackName = name || ens || randomName

  const validateSafeAddress = async (address: string) => {
    if (addedSafes && Object.keys(addedSafes).includes(address)) {
      return 'Safe account is already added'
    }

    try {
      const result = await triggerGetSafe({ chainId: currentChainId, safeAddress: address }).unwrap()
      if (!result) {
        return 'Address given is not a valid Safe account address'
      }
    } catch (error) {
      return 'Address given is not a valid Safe account address'
    }
  }

  const goToNextStep = (data: FormData) => {
    onSubmit({
      ...data,
      [Field.name]: data[Field.name] || fallbackName,
    })

    if (data[Field.name]) {
      trackEvent(LOAD_SAFE_EVENTS.NAME_SAFE)
    }
  }

  const onFormSubmit = handleSubmit((data: FormData) => {
    if (similar) return setPendingData(data)
    goToNextStep(data)
  })

  const onConfirmLookalike = () => {
    if (!pendingData) return
    trackEvent(ADDRESS_POISONING_EVENTS.WARNING_CONTINUED, lookalikeTracking)
    setPendingData(undefined)
    goToNextStep(pendingData)
  }

  const onCancelLookalike = () => {
    if (!pendingData) return
    trackEvent(ADDRESS_POISONING_EVENTS.WARNING_DISMISSED, {
      ...lookalikeTracking,
      [MixpanelEventParams.DISMISS_ACTION]: DismissAction.CANCEL,
    })
    setPendingData(undefined)
  }

  const handleBack = () => {
    const formData = getValues()
    onBack({
      ...formData,
      [Field.name]: formData.name || fallbackName,
    })
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onFormSubmit}>
        <div className={layoutCss.row}>
          <div className="mb-6 flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_220px] md:items-end">
              <div className="order-last md:order-first">
                <NameInput
                  name={Field.name}
                  label={errors?.[Field.name]?.message || 'Name'}
                  placeholder={fallbackName}
                  InputLabelProps={{ shrink: true }}
                  inputSize="hero"
                  variant="surface"
                  InputProps={{
                    endAdornment: resolving ? (
                      <div className="flex items-center">
                        <Spinner className="size-5" />
                      </div>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <span className="flex items-center">
                              <InfoIcon className="size-5" />
                            </span>
                          }
                        />
                        <TooltipContent>
                          This name is stored locally and will never be shared with us or any third parties.
                        </TooltipContent>
                      </Tooltip>
                    ),
                  }}
                />
              </div>
              <div className="order-first w-full md:order-last md:w-[220px]">
                <NetworkSelector triggerClassName={networkSelectTriggerClassName} />
              </div>
            </div>

            <AddressInput
              data-testid="address-section"
              label="Safe account"
              validate={validateSafeAddress}
              name={Field.address}
            />

            {similar && (
              <SimilarSavedAddressNotice subject="This Safe account" enteredAddress={safeAddress} similar={similar} />
            )}
          </div>

          <Typography className="mt-8 block">
            By continuing you consent to the{' '}
            <ExternalLink href={TERMS_URL} noIcon className="[&_span]:underline [&_span]:decoration-primary/40">
              terms of use
            </ExternalLink>{' '}
            and{' '}
            <ExternalLink href={PRIVACY_URL} noIcon className="[&_span]:underline [&_span]:decoration-primary/40">
              privacy policy
            </ExternalLink>
            .
          </Typography>
        </div>

        <Separator />

        <div className={layoutCss.row}>
          <div className="flex justify-between gap-2">
            <Button type="button" variant="outline" size="lg" onClick={handleBack}>
              Back
            </Button>
            <Button data-testid="load-safe-next-btn" type="submit" size="lg" disabled={!isValid}>
              Next
            </Button>
          </div>
        </div>
      </form>

      <SimilarityConfirmDialog
        open={!!pendingData}
        safe={{ address: pendingData?.[Field.address] ?? safeAddress, name: pendingData?.[Field.name] || undefined }}
        addressLabel="Safe account"
        description={`This Safe account is similar to ${similar?.source === 'accounts' ? 'a Safe in your accounts' : 'an address in your address book'}. This could indicate an address poisoning attack.`}
        onConfirm={onConfirmLookalike}
        onCancel={onCancelLookalike}
      />
    </FormProvider>
  )
}

export default SetAddressStep
